// ============================================
// VFX Shared Types and Constants
// ============================================

import { PARTICLE_LAYOUT, UNIFORMS_LAYOUT, VFX_VARIABLES_LAYOUT } from './compiler/Layouts';

// ============================================
// Constants (derived from layouts)
// ============================================

/** Size of particle struct in bytes (aligned to 16) */
export const PARTICLE_STRUCT_SIZE = PARTICLE_LAYOUT.size;

/** Size of uniform buffer in bytes */
export const VFX_UNIFORMS_SIZE = UNIFORMS_LAYOUT.size;

/** Size of VFX variables buffer in bytes */
export const VFX_VARIABLES_SIZE = VFX_VARIABLES_LAYOUT.size;

/** Maximum number of float variables */
export const VFX_MAX_FLOAT_VARS = 16;
/** Maximum number of vec2 variables */
export const VFX_MAX_VEC2_VARS = 8;
/** Maximum number of vec3 variables */
export const VFX_MAX_VEC3_VARS = 8;
/** Maximum number of color variables */
export const VFX_MAX_COLOR_VARS = 8;

// ============================================
// Types
// ============================================

/**
 * Spawn configuration extracted from graph
 */
export interface SpawnInfo {
  /** Initial burst count (0 = no burst) */
  burstCount: number;
  /** Continuous spawn rate (particles per second, 0 = no continuous) */
  spawnRate: number;
  /** Burst delay before first spawn */
  burstDelay: number;
  /** Burst repeat count */
  burstRepeat: number;
  /** Interval between repeated bursts */
  burstInterval: number;
  /** If true, spawns from GPU events instead of direct spawn */
  eventDriven: boolean;
  /** Event ID to listen to (if eventDriven) */
  eventId: number;
}

/**
 * Compiled VFX graph data from the content pipeline.
 */
export interface CompiledVFXData {
  /** WGSL compute shader for particle initialization */
  initShader: string;
  /** WGSL compute shader for particle update */
  updateShader: string;
  /** WGSL/GLSL vertex shader */
  vertexShader: string;
  /** WGSL/GLSL fragment shader */
  fragmentShader: string;
  /** WGSL particle struct definition */
  particleStruct: string;
  /** Maximum number of particles */
  maxParticles: number;
  /** Spawn configuration */
  spawnInfo: SpawnInfo;
  /** Any warnings from compilation */
  warnings?: string[];
  /** Whether fragment shader samples textures */
  usesTexture?: boolean;
  /** Texture variable slot index (0-7) if texture is used */
  textureSlot?: number;
}

/**
 * Particle data structure matching WGSL layout.
 * 32 bytes per particle (aligned for GPU).
 */
export interface ParticleGPUData {
  // vec3 position (12 bytes) + padding (4 bytes)
  positionX: number;
  positionY: number;
  positionZ: number;
  _pad0: number;
  // vec3 velocity (12 bytes) + padding (4 bytes)
  velocityX: number;
  velocityY: number;
  velocityZ: number;
  _pad1: number;
  // vec4 color (16 bytes)
  colorR: number;
  colorG: number;
  colorB: number;
  colorA: number;
  // float size, float rotation, float lifetime, float age (16 bytes)
  size: number;
  rotation: number;
  lifetime: number;
  age: number;
  // uint32 alive flag + padding (16 bytes total for alignment)
  alive: number;
  seed: number;
  _pad2: number;
  _pad3: number;
}

/**
 * Uniform buffer layout for VFX compute shaders.
 */
export interface VFXUniforms {
  /** Delta time in seconds */
  deltaTime: number;
  /** Total elapsed time */
  time: number;
  /** Target spawn count this frame */
  spawnCount: number;
  /** Maximum particles in buffer */
  maxParticles: number;
  /** Random seed */
  randomSeed: number;
}

// ============================================
// Error Types
// ============================================

/**
 * Error thrown when VFX Graph features are not supported.
 */
export class VFXNotSupportedError extends Error {
  readonly code = 'VFX_NOT_SUPPORTED';
  
  constructor(message: string) {
    super(message);
    this.name = 'VFXNotSupportedError';
    
    // Maintain proper prototype chain
    Object.setPrototypeOf(this, VFXNotSupportedError.prototype);
  }
}

// ============================================
// Utility Functions
// ============================================

import type { IRenderContext } from '@engine/render2d';

/**
 * Check if VFX Graph is supported on the given render context.
 */
export function isVFXGraphSupported(ctx: IRenderContext): boolean {
  return ctx.supportsCompute;
}

/**
 * Get a user-friendly message about VFX Graph support.
 */
export function getVFXSupportMessage(ctx: IRenderContext): string {
  if (ctx.supportsCompute) {
    return 'VFX Graph is supported with WebGPU compute shaders.';
  }
  
  return 'VFX Graph is not supported on this system.';
}
